import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  AccessTime,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Refresh,
  RemoveCircle,
  Schedule,
  Visibility,
  Warning,
} from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import { getApiBaseUrl } from "../config";

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 5000;
const ACTIVE_STATUSES = new Set(["WAITING", "RUNNING", "FINALIZING"]);

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontWeight: 600,
    color: "var(--ikaro-text-primary)",
  },
  subtitle: {
    color: "var(--ikaro-text-secondary)",
    fontSize: "0.8rem",
  },
  tableWrapper: {
    border: "1px solid var(--ikaro-border-default)",
    borderRadius: 4,
    overflow: "hidden",
  },
  tableHeaderCell: {
    fontWeight: 600,
    fontSize: "0.72rem",
    color: "var(--ikaro-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderBottom: "1px solid var(--ikaro-border-default)",
  },
  tableRow: {
    transition: "background-color 0.12s ease",
    "& > .MuiTableCell-root": {
      borderBottom: "1px solid var(--ikaro-border-light)",
      fontSize: "0.82rem",
    },
    "&:nth-of-type(even) > .MuiTableCell-root": {
      backgroundColor: "var(--ikaro-bg-surface-secondary)",
    },
    "&:hover > .MuiTableCell-root": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
    },
  },
  statusChip: {
    fontSize: "0.72rem",
    height: "22px",
    fontWeight: 500,
  },
  /* Hint inline para erros que o próprio usuário pode resolver (PDF
     inicial/contestação não encontrados). Aparece logo abaixo do chip de
     status para ser óbvio sem precisar clicar em "ver erro". */
  inlineErrorHint: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 4,
    color: "var(--ikaro-status-warning-text)",
    fontSize: "0.72rem",
    lineHeight: 1.35,
    maxWidth: "32ch",
    "& > svg": {
      flexShrink: 0,
      marginTop: 2,
      fontSize: "0.85rem",
    },
  },
  monoId: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: "0.78rem",
    color: "var(--ikaro-text-primary)",
    fontWeight: 600,
  },
  monoProcesso: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: "0.78rem",
    color: "var(--ikaro-text-primary)",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: "var(--ikaro-text-secondary)",
  },
  paginationRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  paginationButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  errorBlock: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.82rem",
    maxHeight: "55vh",
    overflow: "auto",
    padding: theme.spacing(1.5),
    backgroundColor: "var(--ikaro-status-error-bg)",
    border: "1px solid var(--ikaro-status-error)",
    borderRadius: 4,
  },
}));

const statusIconFor = (status) => {
  switch ((status || "").toUpperCase()) {
    case "DONE":
      return <CheckCircle style={{ fontSize: "1rem" }} />;
    case "RUNNING":
    case "FINALIZING":
      return <Schedule style={{ fontSize: "1rem" }} />;
    case "ERROR":
      return <Warning style={{ fontSize: "1rem" }} />;
    case "FINAL_ERROR":
      return <ErrorIcon style={{ fontSize: "1rem" }} />;
    case "WAITING":
      return <HourglassEmpty style={{ fontSize: "1rem" }} />;
    case "PENDING_APPROVAL":
      return <AccessTime style={{ fontSize: "1rem" }} />;
    case "DISCARDED":
      return <RemoveCircle style={{ fontSize: "1rem" }} />;
    default:
      return <HourglassEmpty style={{ fontSize: "1rem" }} />;
  }
};

const statusColorFor = (status) => {
  switch ((status || "").toUpperCase()) {
    case "DONE":
      return {
        backgroundColor: "var(--ikaro-status-success-bg)",
        color: "var(--ikaro-status-success-text)",
      };
    case "RUNNING":
    case "FINALIZING":
      return {
        backgroundColor: "var(--ikaro-status-running-bg)",
        color: "var(--ikaro-status-running-text)",
      };
    case "ERROR":
    case "WAITING":
    case "PENDING_APPROVAL":
      return {
        backgroundColor: "var(--ikaro-status-warning-bg)",
        color: "var(--ikaro-status-warning-text)",
      };
    case "FINAL_ERROR":
      return {
        backgroundColor: "var(--ikaro-status-error-bg)",
        color: "var(--ikaro-status-error-text)",
      };
    case "DISCARDED":
      return {
        backgroundColor: "var(--ikaro-gray-light)",
        color: "var(--ikaro-gray-dark)",
      };
    default:
      return {
        backgroundColor: "var(--ikaro-gray-light)",
        color: "var(--ikaro-gray-darker)",
      };
  }
};

const TeseImportsHistory = React.forwardRef(({ cellId }, ref) => {
  const classes = useStyles();
  const { t, formatDateTime } = useTranslation("jobs");
  const { token, hasRole } = useAuth();
  const API_BASE_URL = getApiBaseUrl();
  // Erros técnicos ("other") só são úteis ao time de suporte/admin.
  // Erros de PDF errado (inicial/contestação não encontradas) o usuário
  // pode resolver sozinho, então ele sempre tem acesso a esses detalhes.
  const canViewTechnicalError = hasRole("ADMIN", "job_error_viewer");

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDialog, setErrorDialog] = useState(null); // { jobId, description }

  // Trava de concorrência: evita fetches sobrepostos quando o polling dispara
  // enquanto o usuário também clica em "Atualizar" ou troca de página.
  const inFlight = useRef(false);

  const totalPages = useMemo(() => {
    if (!total) return 1;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [total]);

  const fetchPage = useCallback(
    async (targetPage) => {
      if (!API_BASE_URL || !token || !cellId) return;
      if (inFlight.current) return;
      inFlight.current = true;
      setLoading(true);
      setError(null);
      try {
        const url =
          `${API_BASE_URL}/contestacao_teses/imports?cell_id=${encodeURIComponent(
            cellId,
          )}&page=${targetPage}&page_size=${PAGE_SIZE}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setJobs(Array.isArray(data.jobs) ? data.jobs : []);
        setTotal(Number.isFinite(data.total) ? data.total : 0);
      } catch (e) {
        setError(e.message || t("contestacao.cellParams.thesisImportsHistory.loadError"));
      } finally {
        setLoading(false);
        inFlight.current = false;
      }
    },
    [API_BASE_URL, token, cellId, t],
  );

  // Fetch ao montar e quando muda cellId/page.
  useEffect(() => {
    fetchPage(page);
  }, [fetchPage, page]);

  // Polling automático enquanto houver job em estado ativo na página atual.
  // Recria o interval sempre que `jobs` muda, então sai sozinho assim que
  // todos os jobs estiverem em estado terminal.
  useEffect(() => {
    const hasActive = jobs.some((j) =>
      ACTIVE_STATUSES.has((j.status || "").toUpperCase()),
    );
    if (!hasActive) return undefined;
    const id = window.setInterval(() => {
      fetchPage(page);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [jobs, page, fetchPage]);

  // API imperativa para o pai (ex.: chamar refresh após criar um job novo).
  React.useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        // Quando o pai pede refresh, sempre voltamos para a página 1 — o
        // job mais novo aparece no topo.
        if (page !== 1) {
          setPage(1);
        } else {
          fetchPage(1);
        }
      },
    }),
    [page, fetchPage],
  );

  const statusLabel = (status) => {
    switch ((status || "").toUpperCase()) {
      case "WAITING":
        return t("contestacao.cellParams.thesisImportsHistory.statusWaiting");
      case "RUNNING":
      case "FINALIZING":
        return t("contestacao.cellParams.thesisImportsHistory.statusRunning");
      case "DONE":
        return t("contestacao.cellParams.thesisImportsHistory.statusDone");
      case "ERROR":
        return t("contestacao.cellParams.thesisImportsHistory.statusError");
      case "FINAL_ERROR":
        return t("contestacao.cellParams.thesisImportsHistory.statusFinalError");
      case "DISCARDED":
        return t("contestacao.cellParams.thesisImportsHistory.statusDiscarded");
      case "PENDING_APPROVAL":
        return t("contestacao.cellParams.thesisImportsHistory.statusPendingApproval");
      default:
        return status || "—";
    }
  };

  const handleManualRefresh = () => fetchPage(page);
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <Box className={classes.container}>
      <Box className={classes.headerRow}>
        <Box className={classes.headerText}>
          <Typography variant="subtitle1" className={classes.title}>
            {t("contestacao.cellParams.thesisImportsHistory.title")}
          </Typography>
          <Typography className={classes.subtitle}>
            {t("contestacao.cellParams.thesisImportsHistory.subtitle")}
          </Typography>
        </Box>
        <Tooltip title={t("contestacao.cellParams.thesisImportsHistory.refresh")}>
          <span>
            <IconButton
              size="small"
              onClick={handleManualRefresh}
              disabled={loading}
              aria-label={t("contestacao.cellParams.thesisImportsHistory.refresh")}
            >
              {loading ? (
                <CircularProgress size={18} />
              ) : (
                <Refresh fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 8 }}>
          {error}
        </Alert>
      )}

      {jobs.length === 0 && !loading ? (
        <Box className={classes.emptyState}>
          <Typography variant="body2">
            {t("contestacao.cellParams.thesisImportsHistory.empty")}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableWrapper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}>
                  {t("contestacao.cellParams.thesisImportsHistory.tableJobId")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {t("contestacao.cellParams.thesisImportsHistory.tableProcesso")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {t("contestacao.cellParams.thesisImportsHistory.tableStatus")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {t("contestacao.cellParams.thesisImportsHistory.tableUser")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {t("contestacao.cellParams.thesisImportsHistory.tableCreatedAt")}
                </TableCell>
                <TableCell className={classes.tableHeaderCell}>
                  {t("contestacao.cellParams.thesisImportsHistory.tableFinishedAt")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const statusUpper = (job.status || "").toUpperCase();
                const hasError =
                  (statusUpper === "ERROR" || statusUpper === "FINAL_ERROR") &&
                  job.error_description;
                // Só mostra "Finalizado em" quando o job de fato concluiu
                // com sucesso. Em erro/retry/discarded, o final_timestamp
                // está populado no banco mas confunde o usuário, que pensa
                // que o processamento terminou ok.
                const finishedAt =
                  statusUpper === "DONE"
                    ? formatDateTime(job.final_timestamp, { emptyValue: "—" })
                    : "—";
                // Classifica o erro pro visual:
                // - inicial_not_found / contestacao_not_found → erro de
                //   input do usuário, sempre exibe inline o motivo claro
                //   para ele substituir o PDF sem precisar abrir suporte.
                // - other → erro técnico, mostra "Erro técnico"; só admin
                //   com permissão `job_error_viewer` consegue abrir o
                //   dialog com o stack.
                const isUserFixableError =
                  hasError &&
                  (job.error_kind === "inicial_not_found" ||
                    job.error_kind === "contestacao_not_found");
                const userFixableHint = isUserFixableError
                  ? job.error_kind === "inicial_not_found"
                    ? t(
                        "contestacao.cellParams.thesisImportsHistory.inlineInicialNotFound",
                      )
                    : t(
                        "contestacao.cellParams.thesisImportsHistory.inlineContestacaoNotFound",
                      )
                  : null;
                const canOpenErrorDialog =
                  hasError && (isUserFixableError || canViewTechnicalError);
                return (
                  <TableRow key={job.job_id} className={classes.tableRow}>
                    <TableCell>
                      <span className={classes.monoId}>#{job.job_id}</span>
                    </TableCell>
                    <TableCell>
                      <span className={classes.monoProcesso}>
                        {job.numero_processo || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 6 }}
                        >
                          <Chip
                            icon={statusIconFor(job.status)}
                            label={statusLabel(job.status)}
                            className={classes.statusChip}
                            style={statusColorFor(job.status)}
                            size="small"
                          />
                          {canOpenErrorDialog && (
                            <Tooltip
                              title={t(
                                "contestacao.cellParams.thesisImportsHistory.showError",
                              )}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setErrorDialog({
                                    jobId: job.job_id,
                                    description: job.error_description,
                                    errorKind: job.error_kind,
                                  })
                                }
                                aria-label={t(
                                  "contestacao.cellParams.thesisImportsHistory.showError",
                                )}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {userFixableHint && (
                          <Typography
                            variant="caption"
                            className={classes.inlineErrorHint}
                          >
                            <Warning fontSize="inherit" />
                            <span>{userFixableHint}</span>
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{job.user_name || "—"}</TableCell>
                    <TableCell>
                      {formatDateTime(job.insertion_timestamp, { emptyValue: "—" })}
                    </TableCell>
                    <TableCell>{finishedAt}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {total > 0 && (
        <Box className={classes.paginationRow}>
          <Typography variant="caption" color="textSecondary">
            {t("contestacao.cellParams.thesisImportsHistory.totalLabel", { total })}
          </Typography>
          <Box className={classes.paginationButtons}>
            <Typography variant="caption" color="textSecondary">
              {t("contestacao.cellParams.thesisImportsHistory.pageLabel", {
                page,
                total: totalPages,
              })}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={loading || page <= 1}
              onClick={handlePrev}
            >
              {t("contestacao.cellParams.thesisImportsHistory.prev")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={loading || page >= totalPages}
              onClick={handleNext}
            >
              {t("contestacao.cellParams.thesisImportsHistory.next")}
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={Boolean(errorDialog)}
        onClose={() => setErrorDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("contestacao.cellParams.thesisImportsHistory.errorDialogTitle", {
            jobId: errorDialog?.jobId || "",
          })}
        </DialogTitle>
        <DialogContent dividers>
          {/* Mensagem amigável quando o backend conseguiu classificar a
              causa (inicial/contestação não encontradas no PDF). Erro
              técnico segue exibido abaixo para o time de suporte. */}
          {errorDialog?.errorKind === "inicial_not_found" && (
            <Alert severity="warning" style={{ marginBottom: 12 }}>
              {t(
                "contestacao.cellParams.thesisImportsHistory.errorKindInicialNotFound",
              )}
            </Alert>
          )}
          {errorDialog?.errorKind === "contestacao_not_found" && (
            <Alert severity="warning" style={{ marginBottom: 12 }}>
              {t(
                "contestacao.cellParams.thesisImportsHistory.errorKindContestacaoNotFound",
              )}
            </Alert>
          )}
          {errorDialog?.errorKind === "other" && (
            <Alert severity="error" style={{ marginBottom: 12 }}>
              {t(
                "contestacao.cellParams.thesisImportsHistory.errorKindOther",
              )}
            </Alert>
          )}
          <Box className={classes.errorBlock}>
            {errorDialog?.description || "—"}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialog(null)} color="primary">
            {t("contestacao.cellParams.thesisImport.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

TeseImportsHistory.displayName = "TeseImportsHistory";

export default TeseImportsHistory;
